import { useState, useEffect } from 'react';
import { useAppStore } from '../store/useAppStore';
import { 
  sendNotificationToUser, 
  registerFCMToken, 
  unregisterFCMToken,
  NotificationResult 
} from '../utils/firebaseNotifications';

export const useNotifications = () => {
  const { session } = useAppStore();
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if notifications are supported
  useEffect(() => {
    const checkSupport = () => {
      const supported = 'Notification' in window && 'serviceWorker' in navigator;
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
      }
    };

    checkSupport();
  }, []);

  /**
   * Request notification permission and register FCM token
   */
  const requestPermission = async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Notifications are not supported in this browser');
      return false;
    }

    if (!session?.hy_user_id) {
      setError('User session not found');
      return false;
    }

    try {
      setIsRegistering(true);
      setError(null);

      // Request permission
      const permission = await Notification.requestPermission();
      setPermission(permission);

      if (permission !== 'granted') {
        setError('Notification permission denied');
        return false;
      }

      // Initialize Firebase (you'll need to add Firebase SDK)
      // This is a placeholder - you'll need to implement proper Firebase initialization
      console.log('🔥 [FIREBASE] Initializing Firebase...');
      
      // Get FCM token (placeholder implementation)
      // In a real app, you'd use: getToken(messaging, { vapidKey: 'your-vapid-key' })
      const mockToken = `fcm-token-${session.hy_user_id}-${Date.now()}`;
      setFcmToken(mockToken);

      // Register token with backend
      const result = await registerFCMToken(session.hy_user_id, mockToken);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to register FCM token');
      }

      console.log('✅ [NOTIFICATIONS] Successfully registered for notifications');
      return true;

    } catch (error) {
      console.error('💥 [NOTIFICATIONS] Error requesting permission:', error);
      setError(error instanceof Error ? error.message : 'Failed to enable notifications');
      return false;
    } finally {
      setIsRegistering(false);
    }
  };

  /**
   * Unregister from notifications
   */
  const unregister = async (): Promise<boolean> => {
    if (!session?.hy_user_id) {
      setError('User session not found');
      return false;
    }

    try {
      setError(null);

      const result = await unregisterFCMToken(session.hy_user_id);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to unregister FCM token');
      }

      setFcmToken(null);
      console.log('✅ [NOTIFICATIONS] Successfully unregistered from notifications');
      return true;

    } catch (error) {
      console.error('💥 [NOTIFICATIONS] Error unregistering:', error);
      setError(error instanceof Error ? error.message : 'Failed to disable notifications');
      return false;
    }
  };

  /**
   * Send a test notification
   */
  const sendTestNotification = async (): Promise<NotificationResult> => {
    if (!session?.hy_user_id) {
      return { success: false, error: 'User session not found' };
    }

    return sendNotificationToUser({
      hy_user_id: session.hy_user_id,
      title: '🧪 Test Notification',
      body: 'This is a test notification from Hyper ProMax!',
      data: {
        type: 'test',
        timestamp: new Date().toISOString()
      },
      priority: 'normal'
    });
  };

  return {
    isSupported,
    permission,
    fcmToken,
    isRegistering,
    error,
    requestPermission,
    unregister,
    sendTestNotification,
    sendNotificationToUser
  };
};